/**
 * Enhanced API Logging Utility for Debug Overlay
 *
 * This utility creates detailed, collapsible logs for API requests and responses
 * that can be viewed in the debug overlay for better debugging experience.
 */

interface ApiLogData {
	url: string;
	method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
	headers?: Record<string, any>;
	body?: any;
	status?: number;
	responseData?: any;
	error?: any;
	duration?: number;
}

// Global reference to the log function (will be set by DebugOverlay)
let globalLogFunction: ((entry: any) => void) | null = null;

export const setGlobalLogFunction = (logFn: (entry: any) => void) => {
	globalLogFunction = logFn;
};

const extractPath = (url: string): string => {
	try {
		const urlObj = new URL(url);

		return urlObj.pathname;
	} catch {
		// If URL parsing fails, extract path manually
		const pathMatch = url.match(/^(?:https?:\/\/[^\/]+)?(\/.*?)(?:\?.*)?$/);

		return pathMatch ? pathMatch[1] : url;
	}
};

export const logApiRequest = (data: Pick<ApiLogData, "url" | "method" | "headers" | "body">) => {
	if (!globalLogFunction) return;

	const path = extractPath(data.url);

	// Use console.info for API requests
	console.info(`📤 API Request: ${data.method} ${path}`, {
		url: data.url,
		method: data.method,
		headers: data.headers,
		body: data.body,
	});

	globalLogFunction({
		level: "api-request",
		message: `📤 Send request ${data.method} ${path}`,
		collapsibleContent: {
			url: data.url,
			method: data.method,
			headers: data.headers,
			body: data.body,
		},
		expanded: false,
	});
};

export const logApiResponse = (
	data: Pick<ApiLogData, "url" | "method" | "status" | "responseData" | "duration">
) => {
	if (!globalLogFunction) return;

	const path = extractPath(data.url);
	const statusIcon = data.status && data.status >= 200 && data.status < 300 ? "✅" : "❌";
	const durationText = data.duration ? ` (${data.duration}ms)` : "";

	// Use console.info for successful responses, console.warn for client errors
	if (data.status && data.status >= 200 && data.status < 400) {
		console.info(`📥 API Response: ${data.status} ${data.method} ${path}${durationText}`, {
			url: data.url,
			status: data.status,
			responseData: data.responseData,
		});
	} else {
		console.warn(`📥 API Response: ${data.status} ${data.method} ${path}${durationText}`, {
			url: data.url,
			status: data.status,
			responseData: data.responseData,
		});
	}

	globalLogFunction({
		level: "api-response",
		message: `📥 Receive ${data.status} response ${data.method} ${path}${durationText}`,
		collapsibleContent: {
			url: data.url,
			method: data.method,
			status: data.status,
			responseData: data.responseData,
		},
		expanded: false,
	});
};

export const logApiError = (
	data: Pick<ApiLogData, "url" | "method" | "status" | "error" | "duration">
) => {
	if (!globalLogFunction) return;

	const path = extractPath(data.url);
	const durationText = data.duration ? ` (${data.duration}ms)` : "";
	const statusText = data.status ? ` ${data.status}` : "";

	// Use console.error for API errors
	console.error(`❌ API Error:${statusText} ${data.method} ${path}${durationText}`, {
		url: data.url,
		status: data.status,
		error: data.error,
	});

	globalLogFunction({
		level: "api-error",
		message: `❌ Receive${statusText} error ${data.method} ${path}${durationText}`,
		collapsibleContent: {
			url: data.url,
			method: data.method,
			status: data.status,
			responseData: data.error,
		},
		expanded: false,
	});
};
